<script setup lang="ts">
import { Ref, computed } from 'vue';
import { getDefaultLocaleValue } from '@/locales/default';
import type { ComponentPublicInstance } from 'vue';
import { Translations, Weights, TransmissionMode } from '@/types/types';
import WbIcon from '../../../wb/Icon/WbIcon.vue';

const props = defineProps<{
  translations: Translations;
  vehicle: any;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  close: () => void;
  closeButtonRef?: Ref<HTMLElement | null>;
  transmissionMode: TransmissionMode | null;
}>();

const mode = computed(() => props.transmissionMode?.toLocaleLowerCase());

const setCloseButtonRef = (el: Element | ComponentPublicInstance | null) => {
  if (!props.closeButtonRef) return;
  props.closeButtonRef.value = el as HTMLElement | null;
};

const sizeTypeValue = computed(() => {
  const key =
    props.vehicle.sizeType === 'EXTRA_LONG'
      ? 'owvc_tech_data_sizeType_extraLong'
      : `owvc_tech_data_sizeType_${props.vehicle.sizeType.toLowerCase()}`;

  return props.translations[key];
});

const roofTypeValue = computed(() =>
  props.validOptions.roofs
    ? props.translations[
        `owvc_tech_data_roofType_${props.vehicle.roofHeightType.toLowerCase()}`
      ]
    : '',
);

const cabinsTypeValue = computed(() =>
  props.validOptions.cabins
    ? props.translations[
        `owvc_tech_data_cabinType_${props.vehicle.cabinType.toLowerCase()}`
      ]
    : '',
);

const driveTrainTypeValue = computed(() =>
  props.validOptions.drivetrains
    ? props.translations[
        `owvc_tech_data_driveTrain_${props.vehicle.drivetrainType.toLowerCase()}`
      ]
    : '',
);

const engineTypeValue = computed(() =>
  props.validOptions.engines
    ? props.translations[
        `owvc_tech_data_engineType_${props.vehicle.engineType.toLowerCase()}`
      ]
    : '',
);

const transmission = computed<keyof Weights | ''>(() => {
  const m = mode.value as keyof Weights | undefined;
  return m ?? '';
});

const weight = computed(() => {
  const m = mode.value as keyof Weights | undefined;
  if (!m) return '';

  const w = props.vehicle.weights?.[m]?.weightMax;
  if (!w) return '';

  return `${w.value ?? ''} ${w.unit ?? ''}`.trim();
});

const fullTitle = computed(() => {
  const parts = [sizeTypeValue.value];
  if (roofTypeValue.value.length > 1) {
    parts.push(roofTypeValue.value);
  }

  if (cabinsTypeValue.value.length > 1) {
    parts.push(cabinsTypeValue.value);
  }

  if (engineTypeValue.value.length > 1) {
    parts.push(engineTypeValue.value);
  }

  if (props.validOptions.weights) {
    parts.push(weight.value);
  }

  if (driveTrainTypeValue.value.length > 1) {
    parts.push(driveTrainTypeValue.value);
  }

  if (transmission.value.length > 1) {
    parts.push(
      props.translations[`owvc_tech_data_transmission_${transmission.value}`] ||
        transmission.value.charAt(0).toUpperCase() +
          transmission.value.slice(1),
    );
  }

  return `${
    props.translations['owvc_tech_data_technical_data'] ||
    getDefaultLocaleValue('technicalData')
  } (${parts.join(', ')})`;
});
</script>

<template>
  <header class="owvans-tech-data-modal-header" data-sticky="true">
    <h3
      class="wb-text-l-strong owvans-tech-data-modal-header__title"
      style="display: block"
    >
      {{ fullTitle }}
    </h3>
    <button
      type="button"
      :ref="setCloseButtonRef"
      :aria-label="
        translations.owvc_tech_data_aria_label_close_modal ||
        getDefaultLocaleValue('aria_label_close_modal')
      "
      class="wbx-round-button wbx-round-button--small owvans-tech-data-modal-header__close"
      data-test-id="owvans-tech-data-modal-close-button"
      @click="props.close"
    >
      <WbIcon name="bds/close/24" flipRtl />
    </button>
  </header>
</template>

<style lang="scss">
.owvans-tech-data-modal-header {
  display: flex;
  align-items: center;
  padding: var(--wb-spacing-static-smallest) var(--wb-spacing-static-smallest)
    var(--wb-spacing-static-smallest) var(--wb-spacing-static-smallest);
  border-bottom: var(--wb-border-weight-main) solid
    var(--wb-color-border-weak-default);
  background-color: var(--wb-color-section-main);
  color: var(--wb-color-text-main-default);

  &[data-sticky='true'] {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  &__title {
    padding-inline-start: calc(
      var(--wb-spacing-responsive-medium) - var(--wb-spacing-static-smallest)
    );
  }

  &__close {
    margin-inline-start: auto;
  }
}
</style>
